<script setup lang="ts">
import AttachmentImage from '@/components/AttachmentImage.vue'
import FinestButton from '@/components/FinestButton.vue'
import { reportError } from '@/utils/error-reporting'
import AttachmentController from '@controllers/AttachmentController'
import { router } from '@inertiajs/vue3'
import axios from 'axios'
import { useToast } from 'primevue/usetoast'
import { computed, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AttachmentData = App.Data.AttachmentData

const props = defineProps<{
  estateObjectId: string
  photos: AttachmentData[]
}>()

const { t } = useI18n()
const toast = useToast()

const orderedPhotos = computed(() => {
  const primary = props.photos.find((photo) => photo.is_primary)

  return primary ? [primary, ...props.photos.filter((photo) => photo.id !== primary.id)] : props.photos
})

const selectedId = ref<string | null>(null)

const selectedPhoto = computed(
  () => orderedPhotos.value.find((photo) => photo.id === selectedId.value) ?? orderedPhotos.value[0] ?? null,
)

watch(orderedPhotos, (photos) => {
  if (!photos.some((photo) => photo.id === selectedId.value)) {
    selectedId.value = null
  }
})

const fileInput = useTemplateRef<HTMLInputElement>('fileInput')

const uploading = ref(false)
const dropTargetActive = ref(false)

const reportFailure = (error: unknown): void => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined

  if (status === 413) {
    toast.add({ severity: 'error', summary: t('tooLarge'), life: 8000 })

    return
  }

  const message = axios.isAxiosError(error) ? error.response?.data?.message : undefined

  toast.add({ severity: 'error', summary: t('uploadFailed'), detail: message, life: 8000 })

  reportError('Photo upload failed', error)
}

const upload = async (files: File[]): Promise<void> => {
  const accepted = files.filter((file) => file.type.startsWith('image/'))

  if (accepted.length < files.length) {
    toast.add({ severity: 'warn', summary: t('rejected'), life: 5000 })
  }

  if (!accepted.length) {
    return
  }

  const payload = new FormData()
  accepted.forEach((file) => payload.append('files[]', file))

  uploading.value = true

  try {
    await axios.post(
      AttachmentController.upload({
        type: 'photo',
        attachableType: 'estate-object',
        attachableId: props.estateObjectId,
      }).url,
      payload,
    )

    router.reload({ only: ['photos'] })

    toast.add({ severity: 'success', summary: t('uploaded', accepted.length), life: 5000 })
  } catch (error) {
    reportFailure(error)
  } finally {
    uploading.value = false
  }
}

const onSelect = (event: Event): void => {
  const input = event.target as HTMLInputElement

  void upload([...(input.files ?? [])])
  input.value = ''
}

const onDrop = (event: DragEvent): void => {
  dropTargetActive.value = false
  void upload([...(event.dataTransfer?.files ?? [])])
}

const setPrimary = (photo: AttachmentData): void => {
  if (!photo.id) {
    return
  }

  router.patch(AttachmentController.update(photo.id).url, { is_primary: true }, { preserveScroll: true })
}

const deletePhoto = (photo: AttachmentData): void => {
  if (!photo.id) {
    return
  }

  router.delete(AttachmentController.destroy(photo.id).url, { preserveScroll: true })
}
</script>

<template>
  <section
    class="fh-card flex h-full flex-col overflow-hidden"
    :aria-label="t('label')"
    @dragover.prevent="dropTargetActive = !uploading"
    @dragleave.prevent="dropTargetActive = false"
    @drop.prevent="onDrop">
    <div
      v-if="!selectedPhoto"
      class="bg-surface-3 m-6 flex aspect-3/2 w-auto flex-col items-center justify-center gap-3 overflow-hidden rounded-xl border border-dashed p-4 text-center transition-colors"
      :class="dropTargetActive ? 'border-action' : 'border-line'">
      <FinestButton variant="primary" size="md" icon="pi pi-images" :label="t('add')" @click="fileInput?.click()" />
      <p class="text-ink-2 text-xs">{{ t('hint') }}</p>
    </div>

    <!-- Fixed aspect ratio and absolute positioning keep the image from shifting the card while loading. -->
    <div v-else class="flex min-h-0 flex-1 flex-col">
      <div class="group relative aspect-3/2 w-full flex-none overflow-hidden">
        <AttachmentImage
          :src="selectedPhoto.path"
          :alt="selectedPhoto.filename"
          class="absolute inset-0 h-full w-full object-cover" />

        <span
          v-if="selectedPhoto.is_primary"
          class="bg-surface/90 text-ink absolute bottom-3 left-3 rounded-full px-3 py-1 text-xs font-medium">
          {{ t('primary') }}
        </span>

        <div
          class="absolute top-3 right-3 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <button
            v-if="!selectedPhoto.is_primary"
            type="button"
            class="bg-surface/85 text-ink-2 hover:text-action focus-visible:ring-action/35 flex size-8 cursor-pointer items-center justify-center rounded-full backdrop-blur-sm transition-colors focus-visible:ring-[3px] focus-visible:outline-none"
            :aria-label="t('setPrimary')"
            :title="t('setPrimary')"
            @click="setPrimary(selectedPhoto)">
            <i class="pi pi-star text-sm!" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="bg-surface/85 text-ink-2 hover:text-danger focus-visible:ring-action/35 flex size-8 cursor-pointer items-center justify-center rounded-full backdrop-blur-sm transition-colors focus-visible:ring-[3px] focus-visible:outline-none"
            :aria-label="t('delete')"
            :title="t('delete')"
            @click="deletePhoto(selectedPhoto)">
            <i class="pi pi-trash text-sm!" aria-hidden="true" />
          </button>
        </div>

        <div v-if="dropTargetActive" class="ring-action pointer-events-none absolute inset-0 ring-2 ring-inset" />

        <div v-if="uploading" class="bg-surface-0/70 absolute inset-0 flex items-center justify-center" role="status">
          <i class="pi pi-spinner text-action animate-spin text-2xl!" aria-hidden="true" />
          <span class="sr-only">{{ t('uploading') }}</span>
        </div>
      </div>

      <!-- The strip absorbs the leftover card height and scrolls instead of stretching the card. -->
      <div class="flex min-h-0 flex-1 flex-wrap content-start gap-2 overflow-y-auto p-4">
        <button
          v-for="photo in orderedPhotos"
          :key="photo.id"
          type="button"
          class="focus-visible:ring-action/35 relative size-14 flex-none cursor-pointer overflow-hidden rounded-lg border transition-colors focus-visible:ring-[3px] focus-visible:outline-none"
          :class="photo.id === selectedPhoto?.id ? 'border-action' : 'border-line hover:border-line-hover'"
          :aria-label="photo.filename"
          :aria-current="photo.id === selectedPhoto?.id"
          @click="selectedId = photo.id ?? null">
          <AttachmentImage
            :src="photo.path"
            :alt="photo.filename"
            class="absolute inset-0 h-full w-full object-cover" />
        </button>

        <button
          type="button"
          class="border-line text-ink-3 hover:border-action hover:text-action focus-visible:ring-action/35 flex size-14 flex-none cursor-pointer items-center justify-center rounded-lg border border-dashed transition-colors focus-visible:ring-[3px] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="uploading"
          :aria-label="t('add')"
          :title="t('add')"
          @click="fileInput?.click()">
          <i class="pi pi-plus text-xl!" aria-hidden="true" />
        </button>
      </div>
    </div>

    <input ref="fileInput" type="file" class="hidden" accept="image/*" multiple @change="onSelect" />
  </section>
</template>

<i18n>
{
  "de": {
    "label": "Objektbilder",
    "add": "Bilder hinzufügen",
    "hint": "oder Dateien hierher ziehen · JPG, PNG",
    "primary": "Hauptbild",
    "setPrimary": "Als Hauptbild festlegen",
    "delete": "Bild löschen",
    "uploading": "Bilder werden hochgeladen …",
    "uploaded": "Bild hochgeladen | {count} Bilder hochgeladen",
    "uploadFailed": "Die Bilder konnten nicht hochgeladen werden.",
    "tooLarge": "Die Bilder sind zu groß für das Upload-Limit des Servers.",
    "rejected": "Übersprungen: es werden nur Bilddateien hochgeladen."
  },
  "en": {
    "label": "Property photos",
    "add": "Add photos",
    "hint": "or drag files here · JPG, PNG",
    "primary": "Primary photo",
    "setPrimary": "Set as primary photo",
    "delete": "Delete photo",
    "uploading": "Uploading photos …",
    "uploaded": "Photo uploaded | {count} photos uploaded",
    "uploadFailed": "The photos could not be uploaded.",
    "tooLarge": "The photos exceed the server's upload limit.",
    "rejected": "Skipped: only image files are uploaded."
  }
}
</i18n>
